import { useState, useEffect } from 'react';
import { GraduationCap, Stethoscope, Dumbbell, ShoppingBag, Building2, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageSEO from '../components/PageSEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VieQuotidiennePage = () => {
  const [page, setPage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pages/vie-quotidienne`).then(res => setPage(res.data)).catch(() => {});
  }, []);

  const getSection = (key) => page?.sections?.find(s => s.key === key);

  const educationItems = getSection('education_items')?.items || [];
  const santeStructures = getSection('sante_structures')?.items || [];
  const jeunesseItems = getSection('jeunesse_items')?.items || [];
  const commerceLuma = getSection('commerce_luma')?.items || [];

  return (
    <main className="pt-14 md:pt-16" data-testid="vie-quotidienne-page">
      <PageSEO
        title="Vie Quotidienne"
        description="Services aux citoyens de Gandiaye : éducation, santé, jeunesse et sport, commerce et marchés."
        path="/vie-quotidienne"
      />
      {/* Hero */}
      <section className="bg-azure-500 py-16 md:py-24">
        <div className="container-main">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            Vie Quotidienne
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Services aux citoyens : éducation, santé, sport et commerce à Gandiaye.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="container-main">
          <Tabs defaultValue="education" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-start gap-2 bg-neutral-100 p-2 rounded-xl mb-8">
              <TabsTrigger value="education" className="flex-1 md:flex-none" data-testid="tab-education">
                <GraduationCap className="w-4 h-4 mr-2" />
                Éducation
              </TabsTrigger>
              <TabsTrigger value="sante" className="flex-1 md:flex-none" data-testid="tab-sante">
                <Stethoscope className="w-4 h-4 mr-2" />
                Santé
              </TabsTrigger>
              <TabsTrigger value="sport" className="flex-1 md:flex-none" data-testid="tab-sport">
                <Dumbbell className="w-4 h-4 mr-2" />
                Jeunesse & Sport
              </TabsTrigger>
              <TabsTrigger value="commerce" className="flex-1 md:flex-none" data-testid="tab-commerce">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Commerce
              </TabsTrigger>
            </TabsList>

            {/* Éducation */}
            <TabsContent value="education">
              <div className="max-w-4xl">
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  {getSection('education')?.title || 'Éducation à Gandiaye'}
                </h2>
                
                <p className="text-neutral-600 mb-8">
                  {getSection('education')?.content || 'Gandiaye dispose d\'un réseau éducatif complet, du préscolaire au lycée, permettant à tous les enfants d\'accéder à une éducation de qualité.'}
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {educationItems.map((item, index) => (
                    <Card key={index} className="border-l-4 border-azure-500">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-azure-500/10 rounded-xl flex items-center justify-center">
                            <span className="text-3xl font-bold text-azure-500">{item.count}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{item.type}</h3>
                            <p className="text-neutral-500 text-sm">{item.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {getSection('education_bourses') && (
                  <div className="mt-8 p-6 bg-azure-50 rounded-xl">
                    <h3 className="font-heading font-semibold text-lg mb-4">{getSection('education_bourses').title}</h3>
                    <p className="text-neutral-600">
                      {getSection('education_bourses').content}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Santé */}
            <TabsContent value="sante">
              <div className="max-w-4xl">
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  {getSection('sante')?.title || 'Santé à Gandiaye'}
                </h2>
                
                <p className="text-neutral-600 mb-8">
                  {getSection('sante')?.content || 'Un réseau de structures sanitaires assure la prise en charge médicale des habitants de Gandiaye et des villages environnants.'}
                </p>

                <div className="space-y-4">
                  {santeStructures.map((item, index) => (
                    <Card key={index} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-center flex-wrap gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                              <Stethoscope className="text-red-600" size={24} />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{item.name}</h3>
                              <span className="text-sm text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
                                {item.type}
                              </span>
                            </div>
                          </div>
                          <a 
                            href={`tel:${(item.phone || '').replace(/\s/g, '')}`}
                            className="flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700"
                          >
                            <Phone size={18} />
                            {item.phone}
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="mt-8 grid md:grid-cols-2 gap-6">
                  <Card className="bg-red-50 border-none">
                    <CardHeader>
                      <CardTitle className="text-red-700">{getSection('sante_urgences')?.title || 'Urgences Santé'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-neutral-600 mb-4">
                        En cas d'urgence médicale, appelez le :
                      </p>
                      <a href="tel:15" className="text-3xl font-bold text-red-600">15</a>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-emerald-50 border-none">
                    <CardHeader>
                      <CardTitle className="text-emerald-700">{getSection('sante_pharmacie')?.title || 'Pharmacie de Garde'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-neutral-600">
                        {getSection('sante_pharmacie')?.content || 'Pour connaître la pharmacie de garde, contactez le centre de santé ou consultez les affiches en mairie.'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Sport */}
            <TabsContent value="sport">
              <div className="max-w-4xl">
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  {getSection('jeunesse_sport')?.title || 'Jeunesse et Sport'}
                </h2>
                
                <p className="text-neutral-600 mb-8">
                  {getSection('jeunesse_sport')?.content || 'La vie sportive et culturelle est dynamique à Gandiaye, avec des associations actives et des infrastructures dédiées à la jeunesse.'}
                </p>

                <div className="grid md:grid-cols-3 gap-6">
                  {jeunesseItems.map((item, index) => (
                    <Card key={index} className="card-teranga text-center">
                      <CardContent className="p-6">
                        <Dumbbell className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                        <h3 className="font-semibold text-lg">{item.name}</h3>
                        <span className="text-sm text-gold-600 font-medium">{item.type}</span>
                        <p className="text-neutral-500 text-sm mt-2">{item.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {getSection('jeunesse_navetanes') && (
                  <div className="mt-8 p-6 bg-gold-400/10 rounded-xl">
                    <h3 className="font-heading font-semibold text-lg mb-4">{getSection('jeunesse_navetanes').title}</h3>
                    <p className="text-neutral-600">
                      {getSection('jeunesse_navetanes').content}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Commerce */}
            <TabsContent value="commerce">
              <div className="max-w-4xl">
                <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                  {getSection('commerce')?.title || 'Marchés et Commerce'}
                </h2>
                
                <p className="text-neutral-600 mb-8">
                  {getSection('commerce')?.content || 'Gandiaye est un carrefour commercial majeur de la région, avec son célèbre marché hebdomadaire (Luma) qui attire des commerçants de toute la sous-région.'}
                </p>

                <Card className="bg-emerald-50 border-none mb-8">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-6 flex-wrap">
                      <ShoppingBag className="w-16 h-16 text-emerald-600 flex-shrink-0" />
                      <div>
                        <h3 className="text-2xl font-heading font-bold text-emerald-800 mb-2">
                          {getSection('commerce_luma')?.title || 'Le Luma de Gandiaye'}
                        </h3>
                        <p className="text-neutral-600 mb-4">
                          {getSection('commerce_luma')?.content || 'Marché hebdomadaire - L\'un des plus importants de la région de Kaolack.'}
                        </p>
                        {commerceLuma.length > 0 && (
                          <div className="grid sm:grid-cols-2 gap-4 text-sm">
                            {commerceLuma.map((item, i) => (
                              <div key={i}>
                                <span className="font-semibold text-emerald-700">{item.label} :</span>
                                <span className="text-neutral-600 ml-2">{item.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="text-emerald-600" />
                        {getSection('commerce_boutiques')?.title || 'Boutiques de proximité'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-neutral-600">
                        {getSection('commerce_boutiques')?.content || 'Des commerces de proximité sont répartis dans tous les quartiers pour les besoins quotidiens des habitants.'}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ShoppingBag className="text-gold-600" />
                        {getSection('commerce_taxes')?.title || 'Taxes de marché'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-neutral-600">
                        {getSection('commerce_taxes')?.content || 'Les commerçants contribuent au développement communal via les taxes de marché. Tarification disponible en mairie.'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
};

export default VieQuotidiennePage;
